import React, { useEffect, useRef, useState } from 'react';
import EmblaCarousel from 'embla-carousel';

export default function BroadcastCarousel({ broadcasts, onBroadcastClick, desktopCarousel = true }) {
  const mobileRef = useRef(null);
  const desktopRef = useRef(null);
  const tabletRef = useRef(null);
  // Off-screen carousel slides sit inside the viewport's layout box, so
  // loading="lazy" still fetches every one of them on first paint. Only the
  // slides the visitor has actually reached get their image mounted, so the
  // first paint downloads one image instead of all six — at full quality.
  const [revealed, setRevealed] = useState(() => new Set([0, 1]));

  const revealFrom = (index) =>
    setRevealed((prev) => new Set([...prev, index, index + 1, index + 2]));

  // Mobile carousel with auto-scroll (below md breakpoint)
  useEffect(() => {
    if (!mobileRef.current) return;

    const embla = EmblaCarousel(mobileRef.current, {
      loop: true,
      align: 'start',
      slidesToScroll: 1
    });

    embla.on('select', () => revealFrom(embla.selectedScrollSnap()));

    const autoScroll = setInterval(() => {
      embla.scrollNext();
    }, 5000);

    return () => {
      clearInterval(autoScroll);
      embla.destroy();
    };
  }, [broadcasts]);

  // Tablet carousel — 2 at a time with auto-scroll (md to <lg)
  useEffect(() => {
    if (!tabletRef.current) return;

    const embla = EmblaCarousel(tabletRef.current, {
      loop: true,
      align: 'start',
      slidesToScroll: 2
    });

    embla.on('select', () => revealFrom(embla.selectedScrollSnap() * 2));

    const autoScroll = setInterval(() => {
      embla.scrollNext();
    }, 5000);

    return () => {
      clearInterval(autoScroll);
      embla.destroy();
    };
  }, [broadcasts]);

  // Desktop carousel — 4 visible, scrolls one at a time (App Home only)
  useEffect(() => {
    if (!desktopCarousel || !desktopRef.current) return;
    const embla = EmblaCarousel(desktopRef.current, { loop: true, align: 'start', slidesToScroll: 1 });
    const autoScroll = setInterval(() => embla.scrollNext(), 5000);
    return () => {
      clearInterval(autoScroll);
      embla.destroy();
    };
  }, [broadcasts, desktopCarousel]);

  if (broadcasts.length === 0) return null;

  const displayed = broadcasts.slice(0, 6);

  return (
    <div className="mb-6">
      {/* Mobile: carousel (hidden on md+) — tablet-style vertical cards, 1 at a time */}
      <div className="md:hidden" role="region" aria-label="Broadcast Notifications">
        <div className="overflow-hidden" ref={mobileRef}>
          <div className="flex">
            {displayed.map((broadcast, index) =>
              <article key={broadcast.id} className="flex-[0_0_100%] min-w-0 pr-0">
                <div className="rounded-xl overflow-hidden bg-white border border-gray-300 shadow-sm flex flex-col h-full">
                  <div className="px-4 pt-4 pb-3">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <h2 className="font-semibold text-gray-900 text-sm line-clamp-1">{broadcast.title}</h2>
                      {broadcast.link_type && broadcast.link_value &&
                        <button
                          onClick={() => onBroadcastClick(broadcast)}
                          aria-label={`View: ${broadcast.title}`}
                          className="font-medium text-blue-600 hover:text-blue-800 text-xs whitespace-nowrap flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-pink-300 rounded px-1"
                        >
                          {broadcast.link_text || 'View'}
                        </button>
                      }
                    </div>
                    <p className="text-xs text-gray-600 line-clamp-3 w-full">{broadcast.message}</p>
                  </div>
                  {broadcast.image_url &&
                    <button
                      onClick={() => onBroadcastClick(broadcast)}
                      aria-label={`View ${broadcast.title} - press enter to open`}
                      className="w-full h-48 mt-auto focus:outline-none focus:ring-inset focus:ring-2 focus:ring-pink-300 p-0"
                    >
                      {revealed.has(index) ?
                        <img
                          src={broadcast.image_url}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover"
                          aria-hidden="true"
                        /> :
                        <div className="w-full h-full bg-gray-100" aria-hidden="true" />
                      }
                    </button>
                  }
                </div>
              </article>
            )}
          </div>
        </div>
      </div>

      {/* Tablet: carousel, 2 at a time with auto-scroll (md to <lg) */}
      <div className="hidden md:block lg:hidden" role="region" aria-label="Broadcast Notifications">
        <div className="overflow-hidden" ref={tabletRef}>
          <div className="flex">
            {displayed.map((broadcast, index) =>
              <article key={broadcast.id} className="flex-[0_0_50%] min-w-0 pr-4">
                <div className="rounded-xl overflow-hidden bg-white border border-gray-300 shadow-sm flex flex-col h-full">
                  <div className="px-4 pt-4 pb-3">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <h2 className="font-semibold text-gray-900 text-sm line-clamp-1">{broadcast.title}</h2>
                      {broadcast.link_type && broadcast.link_value &&
                        <button
                          onClick={() => onBroadcastClick(broadcast)}
                          aria-label={`View: ${broadcast.title}`}
                          className="font-medium text-blue-600 hover:text-blue-800 text-xs whitespace-nowrap flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-pink-300 rounded px-1"
                        >
                          {broadcast.link_text || 'View'}
                        </button>
                      }
                    </div>
                    <p className="text-xs text-gray-600 line-clamp-3 w-full">{broadcast.message}</p>
                  </div>
                  {broadcast.image_url &&
                    <button
                      onClick={() => onBroadcastClick(broadcast)}
                      aria-label={`View ${broadcast.title} - press enter to open`}
                      className="w-full h-48 mt-auto focus:outline-none focus:ring-inset focus:ring-2 focus:ring-pink-300 p-0"
                    >
                      {revealed.has(index) ?
                        <img
                          src={broadcast.image_url}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover"
                          aria-hidden="true"
                        /> :
                        <div className="w-full h-full bg-gray-100" aria-hidden="true" />
                      }
                    </button>
                  }
                </div>
              </article>
            )}
          </div>
        </div>
      </div>

      {/* Desktop: 4-up carousel (App Home) or grid (lg+) */}
      <div className={desktopCarousel ? 'hidden lg:block overflow-hidden' : 'hidden lg:!grid grid-cols-4 gap-4'} ref={desktopCarousel ? desktopRef : undefined} role="region" aria-label="Broadcast Notifications">
        <div className={desktopCarousel ? 'flex' : 'contents'}>
        {displayed.map((broadcast) =>
          <div key={broadcast.id} className={desktopCarousel ? 'flex-[0_0_25%] min-w-0 pr-4' : 'contents'}>
          <article className="h-full rounded-xl overflow-hidden bg-white border border-brand-navy-deep shadow-sm flex flex-col">
            <div className="px-4 pt-4 pb-3">
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <h2 className="font-semibold text-gray-900 text-sm line-clamp-1">{broadcast.title}</h2>
                {broadcast.link_type && broadcast.link_value &&
                  <button
                    onClick={() => onBroadcastClick(broadcast)}
                    aria-label={`View: ${broadcast.title}`}
                    className="font-medium text-blue-600 hover:text-blue-800 text-xs whitespace-nowrap flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-pink-300 rounded px-1"
                  >
                    {broadcast.link_text || 'View'}
                  </button>
                }
              </div>
              <p className="text-xs text-gray-600 line-clamp-3 w-full">{broadcast.message}</p>
            </div>
            {broadcast.image_url &&
              <button
                onClick={() => onBroadcastClick(broadcast)}
                aria-label={`View ${broadcast.title} - press enter to open`}
                className={`w-full mt-auto focus:outline-none focus:ring-inset focus:ring-2 focus:ring-pink-300 p-0 ${desktopCarousel ? 'h-72' : 'h-48'}`}
              >
                <img
                  src={broadcast.image_url}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                  aria-hidden="true"
                />
              </button>
            }
          </article>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}